import { SinglePageLayoutContext } from "layout/SinglePageLayout";
import { useContext, useEffect, createContext, useState } from "react";
import { useParams, useNavigate } from "react-router";
import labels from "utils/LocalizedStrings";
import Steps from "common/Steps/Steps";
import SelectElection from "./SelectElection";
import SubmitElection from "./SubmitElection";
import sanitize from "utils/SanitizeHtml";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Link,
  Image,
} from "@react-pdf/renderer";
import {
  amountFormatter,
  getPDFFooter,
  getPDFHeader,
  getPDFStylesheet,
  parseHtmlToPDFText,
} from "utils/Utilities";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

const today = new Date();
today.setHours(0, 0, 0, 0);
const futureDate = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() + 2
);
const styles = getPDFStylesheet(StyleSheet);

const MonthlyPayoutElection = () => {
  const singlePageLayoutContext = useContext(SinglePageLayoutContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const participantDetails = useSelector(
    (state) => state?.authReducer?.participantDetails
  );
  const investmentDetails = useSelector(
    (state) => state?.commonReducer?.investmentDetailsInStore
  );

  const [selectedDate, setSelectedDate] = useState({
    day: futureDate.getDate(),
    month: futureDate.getMonth() + 1,
    year: futureDate.getFullYear(),
  });
  const [payout, setPayout] = useState({});

  const MyPdfDocument = () => (
    <Document>
      <Page style={styles.page}>
        {getPDFHeader(View, Image, styles, Text, participantDetails)}

        <Text style={styles.sectionTitle}>
          {labels.monthlyPayoutElection.monthlyInstallmentPayouts}
        </Text>

        <Text style={styles.paragraph}>
          {labels.monthlyPayoutElection.basedUponStartDate}
        </Text>

        <View style={styles.row}>
          <Text style={styles.label}>
            {labels.monthlyPayoutElection.startDatePayout}
          </Text>
          <Text>{payout?.payoutStartDate}</Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.label}>
            {labels.monthlyPayoutElection.initialWithdrawal}
          </Text>
          <Text>
            {payout?.currentWdlPeriodStartDate +
              " - " +
              payout?.currentWdlPeriodEndDate}
          </Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.label}>
            {labels.monthlyPayoutElection.estimatedMonthly}
          </Text>
          <Text>{amountFormatter(payout?.estimatedMonthlyPayoutAmount)}</Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.label}>
            {labels.monthlyPayoutElection.nextWithdrawal}
          </Text>
          <Text>
            {payout?.nextWdlPeriodStartDate +
              " - " +
              payout?.nextWldPeriodEndDate}
          </Text>
        </View>

        <View style={[styles.row, { marginBottom: 12 }]}>
          <Text style={styles.label}>
            {labels.monthlyPayoutElection.resetDate}
          </Text>
          <Text>{payout?.monthlyPayoutResetDate}</Text>
        </View>

        <Text style={styles.paragraph}>
          {labels.monthlyPayoutElection.paymentMayDiffer}
        </Text>
        <Text style={styles.paragraph}>
          {labels.monthlyPayoutElection.vestingMsg}
        </Text>

        <Text style={styles.sectionTitle}>
          {labels.monthlyPayoutElection.taxWithholding}
        </Text>

        <Text style={styles.paragraph}>
          {parseHtmlToPDFText(
            labels.monthlyPayoutElection.taxesMsg,
            Text,
            Link
          )}
        </Text>

        <Text style={styles.sectionTitle}>
          {labels.monthlyPayoutElection.paymentDelivery}
        </Text>

        <Text style={styles.paragraph}>
          {parseHtmlToPDFText(
            labels.monthlyPayoutElection.paymentDeliveryMsg,
            Text,
            Link
          )}
        </Text>

        {getPDFFooter(View, styles, Text)}
      </Page>
    </Document>
  );

  const cancel = () => {
    navigate(-1);
  };

  const MonthlyPayoutElectionContext = createContext({
    labels,
    selectedDate,
    setSelectedDate,
    navigate,
    sanitize,
    PDFDownloadLink,
    MyPdfDocument,
    participantDetails,
    investmentDetails,
    payout,
    cancel,
  });

  useEffect(() => {
    if (location?.state?.currentWdlPeriodStartDate) {
      setPayout(location?.state);
    }
  }, [location.state]);

  useEffect(() => {
    singlePageLayoutContext({
      title: labels?.monthlyPayoutElection?.title,
      buttonText: labels?.monthlyPayoutElection?.cancel,
      buttonPath: "/dashboard",
    });
  }, []);

  if (id == 1) {
    return (
      <section className="single-page-container">
        <Steps current="1" total="2" />
        <SelectElection context={MonthlyPayoutElectionContext} />
      </section>
    );
  } else if (id == 2) {
    return (
      <section className="single-page-container">
        <Steps current="2" total="2" />
        <SubmitElection context={MonthlyPayoutElectionContext} />
      </section>
    );
  }
};

export default MonthlyPayoutElection;
